import { AudioEngineState } from ".";
import { ActivePlaylist, Playlist, DecodedTrack, ActiveTrack } from "../../../config/audio/types";
import { LOGS } from "../logging";
import { activePlaylistNeedsAdjustment, calculateTrackGains } from "./audioCalculations";
import {
	decodeTracks,
	webAudioGainSet,
	webAudioNodesCreate,
	webAudioNodeCreate,
	webAudioTrackStart,
	webAudioTrackStop,
} from "./webAudioPlayerEffects";

export interface AudioEngine {
	activePlaylists: readonly ActivePlaylist[];
	state: AudioEngineState;
	status: AudioContextState | "uninitialized";
	initialized: boolean;
	init: () => Promise<AudioContext>;
	getPlaylist: (name: string) => ActivePlaylist | null;
	destroy: () => Promise<void>;
	startPlaylist: (playlist: Playlist) => Promise<void>;
	stopPlaylist: (playlistName: string) => Promise<void>;
	playOneShot: (playlist: Playlist) => Promise<void>;
	updatePlaylist: (current: ActivePlaylist, next: Playlist) => void;
}
/**
 * Core Audio Engine - Manages AudioContext
 * This encapsulates all WebAudio API concerns in one place
 */
export class WebAudioApiEngine implements AudioEngine {
	private audioContext: AudioContext | null = null;
	private _state: AudioEngineState = { activePlaylists: new Map() };

	/**
	 * all active playlists
	 */
	get activePlaylists(): readonly ActivePlaylist[] {
		return Array.from(this._state.activePlaylists.values());
	}

	/**
	 * @returns status of the underlying WebAudio context or "uninitialized" if
	 * no WebAudio Context state found
	 */
	get status() {
		return this.audioContext?.state ?? "uninitialized";
	}

	/**
	 * @returns a copy of the internal engine state
	 */
	get state() {
		return {
			activePlaylists: new Map(this._state.activePlaylists),
		};
	}

	get initialized() {
		return this.audioContext !== null;
	}

	constructor() {
		// AudioContext creation is deferred until first user gesture
	}
	/**
	 * Initialize audio context - must be called after user gesture
	 */
	async init() {
		if (!this.audioContext) {
			this.audioContext = new AudioContext();
		}

		if (this.audioContext.state === "suspended") {
			await this.audioContext.resume();
		}

		return this.audioContext;
	}

	/**
	 * Get playlist by name
	 * @returns Playlist data if found otherwise null
	 */
	getPlaylist(name: string) {
		return this._state.activePlaylists.get(name) ?? null;
	}

	/**
	 * Clean up resources
	 */
	async destroy(): Promise<void> {
		if (this.audioContext && this.audioContext.state !== "closed") {
			await this.audioContext.close();
		}
		this.audioContext = null;
		this._state = { activePlaylists: new Map() };
	}

	async startPlaylist(playlist: Playlist): Promise<void> {
		if (!this.audioContext) {
			throw new Error("Audio context not initialized");
		}

		const decodedTracks = await decodeTracks(playlist.playlist, this.audioContext);
		const trackNodes = webAudioNodesCreate(decodedTracks, this.audioContext, playlist.gain);
		const tracksWithChain = this.setupTrackChaining(trackNodes);

		if (tracksWithChain.length <= 0) {
			return;
		}

		this.setupLooping(tracksWithChain, playlist, decodedTracks);

		const activePlaylist = { ...playlist, tracks: [...tracksWithChain] };

		webAudioTrackStart(tracksWithChain[0]);

		this.updateState((state) => ({
			...state,
			activePlaylists: new Map(state.activePlaylists).set(playlist.name, activePlaylist),
		}));
	}

	async stopPlaylist(playlistName: string): Promise<void> {
		const playlist = this.getPlaylist(playlistName);
		if (playlist) {
			playlist.tracks.map(webAudioTrackStop);

			this.updateState((state) => ({
				...state,
				activePlaylists: new Map(
					[...state.activePlaylists].filter(([name]) => name !== playlistName),
				),
			}));
		}
	}

	async updatePlaylist(current: ActivePlaylist, next: Playlist) {
		LOGS.AUDIO && console.log(`Adjusting the playlist: ${next.name}`);

		if (!activePlaylistNeedsAdjustment(current, next)) return;

		const newGains = calculateTrackGains(current, next);

		current.tracks.forEach((track, index) => webAudioGainSet(track, newGains[index]));

		const updatedPlaylist = { ...current, gain: next.gain, isLooping: next.isLooping };

		this.updateState((state) => ({
			...state,
			activePlaylists: new Map(state.activePlaylists).set(next.name, updatedPlaylist),
		}));
	}

	async playOneShot(playlist: Playlist) {
		if (!this.audioContext) {
			throw new Error("Audio context not initialized");
		}

		const decodedTracks = await decodeTracks(playlist.playlist, this.audioContext);
		const trackNodes = webAudioNodesCreate(decodedTracks, this.audioContext, playlist.gain);
		const tracksWithChain = this.setupTrackChaining(trackNodes);

		if (tracksWithChain.length <= 0) {
			return;
		}

		webAudioTrackStart(tracksWithChain[0]);
		// Note: One-shot playlists are not added to the engine state
		// this prevents them from being stopped or adjusted by state changes
	}

	/**
	 * Applies updater function transforming current state to new state
	 */
	private updateState(updater: (state: Readonly<AudioEngineState>) => AudioEngineState) {
		this._state = updater(this._state);
	}

	/**
	 * Setup looping for a playlist
	 */
	private setupLooping(
		tracks: readonly ActiveTrack[],
		playlist: Playlist,
		decodedTracks: readonly DecodedTrack[],
	) {
		if (!playlist.isLooping || tracks.length === 0) return;

		const lastTrack = tracks[tracks.length - 1];
		lastTrack.sourceNode.onended = () => {
			if (!this.audioContext) {
				console.warn("Audio context not available for looping");
				return;
			}

			const newTrackNodes = decodedTracks.map((decodedTrack) =>
				webAudioNodeCreate(decodedTrack, this.audioContext!, playlist.gain),
			);

			const chainedTracks = this.setupTrackChaining(newTrackNodes);

			if (chainedTracks.length <= 0) {
				LOGS.AUDIO && console.warn("No chained tracks available for looping");
				return;
			}

			// CRITICAL: Set up looping on the new tracks too!
			this.setupLooping(chainedTracks, playlist, decodedTracks);

			webAudioTrackStart(chainedTracks[0]);

			const updatedPlaylist = { ...playlist, tracks: [...chainedTracks] };

			this.updateState((state) => ({
				...state,
				activePlaylists: new Map(state.activePlaylists).set(playlist.name, updatedPlaylist),
			}));
		};
	}

	/**
	 * Setup track chaining without looping logic
	 */
	private setupTrackChaining(tracks: readonly ActiveTrack[]): readonly ActiveTrack[] {
		tracks.forEach((track, index) => {
			const isLastTrack = index === tracks.length - 1;

			if (!isLastTrack) {
				track.sourceNode.onended = () => webAudioTrackStart(tracks[index + 1]);
			}
		});

		return tracks;
	}
}
